import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CSVLink } from 'react-csv';
import {
  Badge,
  Button,
  Icon,
  InputAdornment,
  FormHelperText,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import { getStoreCustomersGridApi } from 'api';
import ViewColumn from 'helpers/ViewColumn';


const useStyles = makeStyles({
  bgBlue: {
    backgroundColor: '#E7F0FF',
  },
  customSearch: {
    borderTopLeftRadius: '6px',
    borderBottomLeftRadius: '6px',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E7F0FF'
    },
    '& .MuiInputBase-input::-webkit-input-placeholder': {
      color: '#4B6FC2',
      opacity: '1'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4B6FC2'
    }
  },
  customSearchField: {
    borderTopRightRadius: '6px',
    borderBottomRightRadius: '6px',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E7F0FF'
    },
    '& .MuiInputBase-input::-webkit-input-placeholder': {
      color: '#4B6FC2',
      opacity: '1'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4B6FC2'
    },
    '& .MuiInputAdornment-root .MuiTypography-colorTextSecondary': {
      color: '#4B6FC2'
    }
  },
  helperText: {
    position: 'absolute',
    bottom: '-15px',
    left: '25px'
  },
  filterBtn: {
    padding: '7px 15px',
    marginLeft: '10px'
  }
});

const CustomTableToolbar = (
  {
    onSearch,
    setOpenFilter,
    filterConditions,
    tableCells,
    setHeadTableCells
  }: ICustomToolbar
) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [searchField, setSearchField] = useState('name');
  const selectedConditions = Object.values(filterConditions)
    .filter((condition) => !!condition?.value.length);

  const { customersCSVData, customersTotalCount } = useSelector((state: IReduxState) => state.customers);

  useEffect(() => {
    if (!customersCSVData.length) {
      getStoreCustomersGridApi({
        dispatch,
        sortDirection: 'desc',
        sortField: 'created_at',
        pageSize: customersTotalCount,
        currentPage: 1,
        filterConditions: [],
        searchConditions: [],
        isCSVData: true
      });
    }
  }, [customersTotalCount]);

  return (
    <Toolbar className="w-full relative">
      <div className="flex-column w-full">
        <div className="flex items-center justify-between w-full">
          <div className="w-full flex items-center">
            <TextField
              variant="outlined"
              className={`w-full max-w-300 text-primary ${classes.bgBlue} ${classes.customSearch}`}
              name="search-orders-value"
              placeholder="Search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onSearch(searchValue, searchField);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {!searchValue.length ?
                      <Icon color="primary">search</Icon>
                      :
                      <Icon
                        color="primary"
                        className="cursor-pointer"
                        onClick={() => {
                          setSearchValue(() => '');
                          onSearch('', 'name');
                        }}
                      >
                        close
                      </Icon>
                    }
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              variant="outlined"
              className={`w-full max-w-200 mr-3 text-primary ${classes.bgBlue} ${classes.customSearchField}`}
              name="search-orders-field"
              value={searchField}
              onChange={(event) => {
                setSearchField(event.target.value);

                if (searchValue) {
                  onSearch(searchValue, event.target.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">by</InputAdornment>
                )
              }}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="billing_full">Billing Address</MenuItem>
              <MenuItem value="billing_telephone">Phone</MenuItem>
            </TextField>
            <Button
              color="primary"
              variant="contained"
              className="mr-2 px-3 min-w-120 min-h-40"
              onClick={() => onSearch(searchValue, searchField)}
            >
              <Icon className="mr-2">search</Icon>
              <span className="uppercase">Search</span>
            </Button>
          </div>
          <div className="w-full flex items-center justify-end">
            <ViewColumn
              storageKey="customersTableCells"
              tableCells={tableCells}
              setHeadTableCells={setHeadTableCells}
            />
            <Tooltip title="Download CSV">
              <CSVLink
                data={customersCSVData}
                className="flex items-center"
                filename={'customers.csv'}
                onClick={() => !!customersCSVData.length}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!customersCSVData.length}
                  className={classes.filterBtn}
                >
                  <Icon className={`${!!customersCSVData.length ? 'text-primary' : null} mr-2`}>
                    cloud_download
                  </Icon>
                  <span className={`${!!customersCSVData.length ? 'text-brand' : ''}`}>
                    Download
                  </span>
                </Button>
              </CSVLink>
            </Tooltip>
            <Badge
              badgeContent={selectedConditions.length}
              color="primary"
              overlap="rectangular"
            >
              <Tooltip title="Filter by">
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.filterBtn}
                  onClick={() => setOpenFilter(true)}
                >
                  <Icon className="text-primary mr-2">
                    filter_list
                  </Icon>
                  <span className="text-brand">Filter By</span>
                </Button>
              </Tooltip>
            </Badge>
          </div>
        </div>
        {!!searchValue.length &&
        <FormHelperText className={classes.helperText}>
          <Typography variant="caption">
            Press &apos;Enter&apos; to search
          </Typography>
        </FormHelperText>
        }
      </div>
    </Toolbar>
  );
};

export default CustomTableToolbar;
